// src/helpers/schedule/awp_summary.js
import {
  sum, num, getExchangeRate, getTolerance,
  minCostWithTol, maxCostWithTol
} from './config';

// --- generic dfs utilities ---
const isObj = (v) => v && typeof v === 'object';

const collectDivisionNodes = (root) => {
  const out = [];
  const walk = (n) => {
    if (!isObj(n)) return;
    // Heuristic: a Division/Catalog node typically has price/amount fields or children
    const looksLikeDivision =
      'Awp' in n || 'AWP' in n || 'TotalPrice' in n || 'HasLabor' in n || 'HasEquipment' in n;

    if (looksLikeDivision) out.push(n);

    Object.keys(n).forEach((k) => {
      const v = n[k];
      if (Array.isArray(v)) v.forEach(walk);
      else if (isObj(v)) walk(v);
    });
  };
  walk(root);
  return out;
};

const readAwp = (o) => (o?.Awp ?? o?.AWP ?? '').toString().trim();
const readDesc = (o) =>
  (o?.Description ?? o?.Name ?? o?.Category ?? o?.Division ?? '').toString().trim();

// C# equivalents:
//  - item.TotalPrice (+ labor + equipment if present)
//  - item.TotalLaborPrice when item.HasLabor
//  - item.TotalEquipmentPrice when item.HasEquipment
const nodeTotals = (n) => {
  const mat = num(n?.TotalPrice);
  const lab = n?.HasLabor ? num(n?.TotalLaborPrice) : 0;
  const eqp = n?.HasEquipment ? num(n?.TotalEquipmentPrice) : 0;
  return { mat, lab, eqp, total: mat + lab + eqp };
};

// Group by AWP; move empty AWP to the end like your C# “otherGroup”
export const buildAwpSummaryFromDivisions = (json, project) => {
  const rate = getExchangeRate(project);
  const tol = getTolerance(project);

  const divisions = json?.Divisions || json?.Project?.Divisions || json?.Estimate?.Divisions;
  if (!divisions) return { byCode: {}, order: [] };

  const nodes = collectDivisionNodes(divisions);
  const byCodeRaw = {};
  nodes.forEach((n) => {
    const code = readAwp(n); // may be ''
    const dsc = readDesc(n);
    const { mat, lab, eqp, total } = nodeTotals(n);

    if (!byCodeRaw[code]) {
      byCodeRaw[code] = {
        code,
        descriptions: new Set(),
        totalMat: 0,
        totalLab: 0,
        totalEqp: 0,
        total: 0,
      };
    }
    byCodeRaw[code].descriptions.add(dsc);
    byCodeRaw[code].totalMat += mat;
    byCodeRaw[code].totalLab += lab;
    byCodeRaw[code].totalEqp += eqp;
    byCodeRaw[code].total += total;
  });

  // apply exchange rate + tolerance bands
  const byCode = {};
  Object.keys(byCodeRaw).forEach((code) => {
    const r = byCodeRaw[code];
    const totalXR = r.total * rate;
    byCode[code || 'UNASSIGNED'] = {
      code: code || 'UNASSIGNED',
      description: Array.from(r.descriptions).find(Boolean) || (code ? `AWP ${code}` : 'Others'),
      totalMat: r.totalMat * rate,
      totalLab: r.totalLab * rate,
      totalEqp: r.totalEqp * rate,
      total: totalXR,
      minTotal: minCostWithTol(totalXR, tol),
      maxTotal: maxCostWithTol(totalXR, tol),
      tolerancePct: tol,
      exchangeRate: rate,
    };
  });

  // order: put non-empty AWP first, empty (UNASSIGNED) last
  const order = Object.keys(byCode).sort((a, b) => {
    const aa = a === 'UNASSIGNED' ? 1 : 0;
    const bb = b === 'UNASSIGNED' ? 1 : 0;
    if (aa !== bb) return aa - bb;
    return a.localeCompare(b);
  });

  return { byCode, order };
};
